import type { FeedIntegrationType } from '@/types/feeds';
import type { FilterFieldOption } from '@/types/filters';

import type { NestedGroupFilter } from './ComplexFilterNestedGroup.vue';

export type DataSourceType = FeedIntegrationType | 'crossbeam';

export type FilterMode = 'editable' | 'readOnly' | 'hybrid';

export type GroupedFieldOption<T extends string | number = string | number> = {
  id: string | number;
  label: string;
  useBorder?: boolean;
  options: FilterFieldOption<T>[];
  dataSource?: DataSourceType;
  dataSourceLabel?: string;
  datasetName?: string;
  feedId?: number;
  allowNesting?: boolean;
  /** When true, each filter row excludes fields already selected by other rows */
  excludeSelectedFields?: boolean;
};

export type DataSource<T extends string | number = string | number> = {
  label: string;
  value: DataSourceType;
  groups: {
    title: string;
    sourceId: string | number;
    fields: FilterFieldOption<T>[];
    allowNesting?: boolean;
  }[];
};

export type OwnFieldsConfig = DataSource[] | GroupedFieldOption[];

export type FilterGroupOutput = {
  title: string;
  sourceId: string | number;
  subtitle?: string;
  filters: NestedGroupFilter[];
  expression: string[];
};

// Operators that don't require a comparison value
export const NO_OPERAND_OPERATORS = new Set(['is empty', 'is not empty']);

export function isFilterComplete(filter: Partial<NestedGroupFilter>): boolean {
  if (filter.value === undefined || filter.operator === undefined) {
    return false;
  }

  // "is empty" / "is not empty" don't need an operand
  if (NO_OPERAND_OPERATORS.has(filter.operator)) {
    return true;
  }

  const { operand } = filter;
  if (operand === undefined || operand === null) return false;
  if (Array.isArray(operand)) return operand.length > 0;
  if (
    typeof operand === 'object' &&
    'items' in operand &&
    Array.isArray((operand as { items: unknown[] }).items)
  ) {
    return (operand as { items: unknown[] }).items.length > 0;
  }
  return true;
}
